'use client';

import { useState, useCallback, useRef, useLayoutEffect, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';
import { getGhostSrc } from '../../utils/ghostSkeleton';

interface SkinImageProps {
  /** Market hash name — задаёт ghost-силуэт модели. */
  name: string;
  src?: string | null;
  alt?: string;
  /** Классы на обе картинки (размер/позиция/тень). */
  imgClassName?: string;
  /** Inline-стиль только на реальную картинку (фильтры/трансформы/анимация раскрытия). */
  imgStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
  /** Грузить сразу, не дожидаясь попадания во вьюпорт — для картинки над сгибом
   *  (открытие кейса, экран айтема), где ленивая загрузка добавила бы задержку. */
  priority?: boolean;
}

/**
 * Картинка скина с ghost-заглушкой. Пока грузится (или если не загрузилась)
 * реальная картинка — показываем серый силуэт модели из public/ghosts.
 */
export const SkinImage: FC<SkinImageProps> = ({
  name, src, alt = name, imgClassName, imgStyle, className, style, priority = false,
}) => {
  const ghost = getGhostSrc(name);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const prevSrcRef = useRef(src);

  useLayoutEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setIsLoaded(false);
      setHasFailed(false);
    }
  }, [src]);

  // Уже лежащая в кэше картинка приходит в ref готовой (`complete`) и `onLoad` по ней не
  // сработает — снимаем состояние с самого элемента. Отдельная проба через `new Image()`
  // здесь стояла раньше и стоила лишнего сетевого запроса на каждый скин, мимо ленивой загрузки.
  const captureNode = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) setIsLoaded(true);
  }, []);

  const handleLoad = useCallback(() => setIsLoaded(true), []);
  const handleError = useCallback(() => setHasFailed(true), []);

  const showGhost = !src || !isLoaded || hasFailed;

  return (
    <div className={cn('relative w-full h-full', className)} style={style}>
      {showGhost && (
        <img
          src={ghost}
          alt=""
          aria-hidden
          decoding="async"
          className={cn('absolute inset-0 opacity-50 pointer-events-none', imgClassName)}
        />
      )}
      {src && !hasFailed && (
        <img
          ref={captureNode}
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={handleLoad}
          onError={handleError}
          style={imgStyle}
          className={cn('relative transition-opacity duration-200', isLoaded ? 'opacity-100' : 'opacity-0', imgClassName)}
        />
      )}
    </div>
  );
};
